// Eventos con consumo incluido (backend: docs/spec-eventos-consumo-incluido.md): lo que la
// pantalla necesita saber de "qué incluye" un evento, en funciones PURAS. No piden nada ni
// guardan nada: reciben el resumen que manda el backend (`Summary`: `activity.included` en la
// agenda, `account.eventIncluded` en la cuenta de la mesa, `event.included` en el detalle del
// evento) y lo dejan listo para dibujar.
//
// El número que vale es siempre el del server. Lo único que se calcula acá es el AVISO PREVIO del
// POS (`previewOrder`): cómo quedaría el contador si se registrara el pedido que está en el
// carrito. Repite la regla de asignación del backend (`assignUnits`, FR-026) y las dos se prueban
// contra el mismo archivo de casos (`__fixtures__/assign-cases.json`, copia byte a byte del
// backend): si una cambia sin la otra, el test se pone rojo.

// Las categorías del menú de cafetería, en el orden del mostrador. Son las mismas seis claves que
// valida el backend (`VALID_CATEGORIES`) y las mismas etiquetas que usa el POS
// (`CATEGORY_ORDER` / `CATEGORY_LABELS` de CafeteriaPosPage, que son privadas de esa página).
export const MENU_CATEGORIES = [
    { key: 'cold_drink', label: 'Bebidas frías' },
    { key: 'hot_drink', label: 'Bebidas calientes' },
    { key: 'bakery', label: 'Panadería' },
    { key: 'pizzeria', label: 'Pizzería' },
    { key: 'snack', label: 'Snacks' },
    { key: 'other', label: 'Otros' },
];

const categoryLabel = (key) => {
    const found = MENU_CATEGORIES.find((c) => c.key === key);
    return found ? found.label : String(key);
};

// Lee de un Map o de un objeto plano, con la clave como número o como texto.
const lookup = (source, key) => {
    if (source == null) return undefined;
    if (source instanceof Map) {
        if (source.has(key)) return source.get(key);
        if (source.has(Number(key))) return source.get(Number(key));
        return source.get(String(key));
    }
    return source[key];
};

// Del catálogo de cafetería (`GET cafeteria/catalog`: [{ code, name, category, … }]) al índice
// por código que piden `describeInclusion` y `previewOrder`.
export const menuByCodeOf = (items) => {
    const byCode = {};
    (items || []).forEach((item) => {
        if (item && item.code != null) byCode[item.code] = item;
    });
    return byCode;
};

// El resumen del evento de una fila de la agenda del salón, o null. Null es "esta mesa no es de
// un evento que declare qué incluye" y también "el backend no lo mandó" (uno viejo, o uno al que
// le falló el cálculo): en los dos casos la pantalla la trata como una mesa común (FR-122).
export const includedOf = (row) => {
    const included = row && row.activity ? row.activity.included : null;
    return included != null && typeof included === 'object' ? included : null;
};

// El contador del evento, un texto por renglón: "Pizza 8 de 10". `over` marca los excedidos, que
// la pantalla resalta (FR-061); `stale`, los renglones por ítems con algún ítem que ya no está en
// el menú ("a revisar", FR-012).
export const formatCounter = (summary) => ((summary && summary.rules) || []).map((rule) => {
    const delivered = Number(rule.delivered) || 0;
    const allowance = Number(rule.allowance) || 0;
    return {
        text: `${rule.label} ${delivered} de ${allowance}`,
        over: delivered > allowance,
        stale: !!rule.stale,
    };
});

// De dónde salió una comanda incluida, con las MISMAS palabras en la cuenta de la mesa y en la
// card del evento: "Cargada en el POS por X" / "Pasada a incluido por X". `via` y `includedBy`
// salen del rastro del ticket; si el rastro no se pudo leer (`via` null) no se inventa un origen.
export const includedOriginText = (order) => {
    if (!order) return '';
    const by = order.includedBy ? ` por ${order.includedBy}` : '';
    if (order.via === 'correction') return `Pasada a incluido${by}`;
    if (order.via === 'pos') return `Cargada en el POS${by}`;
    return by ? `Incluida${by}` : '';
};

// Una inclusión en una línea, como se lee en la tabla de tipos de entrada:
// "Pizza · 1 cada 2 personas · categoría Pizzería". `menuByCode` pone el nombre de los ítems; el
// que no está ahí se nombra con lo que guardó el servidor (`inclusion.items`, que lo trae aunque
// el ítem esté oculto o archivado). Sin ningún nombre, se dice que ya no está en el menú: el
// código no se muestra nunca.
export const describeInclusion = (inclusion, menuByCode) => {
    if (!inclusion) return '';
    if (inclusion.matchKind === 'free') return `${inclusion.label} · sólo texto`;

    const quantity = Number(inclusion.quantity) || 0;
    const everyN = Number(inclusion.everyNPeople) || 0;
    const cadence = everyN === 1 ? `${quantity} por persona` : `${quantity} cada ${everyN} personas`;

    const values = inclusion.matchValue || [];
    let against;
    if (inclusion.matchKind === 'category') {
        against = `${values.length === 1 ? 'categoría' : 'categorías'} ${values.map(categoryLabel).join(', ')}`;
    } else {
        const saved = (code) => {
            const item = (Array.isArray(inclusion.items) ? inclusion.items : []).find((i) => i && Number(i.code) === Number(code));
            return item && item.name ? item.name : null;
        };
        const names = [];
        let sinNombre = 0;
        values.forEach((code) => {
            const item = lookup(menuByCode, code);
            const name = (item && item.name) || saved(code);
            if (name) names.push(name);
            else sinNombre += 1;
        });
        if (names.length === 0 && sinNombre > 0) {
            against = sinNombre === 1 ? 'un ítem que ya no está en el menú' : `${sinNombre} ítems que ya no están en el menú`;
        } else {
            if (sinNombre > 0) names.push(sinNombre === 1 ? 'uno que ya no está en el menú' : `${sinNombre} que ya no están en el menú`);
            against = `${values.length === 1 ? 'ítem' : 'ítems'} ${names.join(', ')}`;
        }
    }
    return `${inclusion.label} · ${cadence} · ${against}`;
};

// El reparto que comparten `previewOrder` y `splitOrder`: cada unidad del pedido, a su renglón.
// `overflowToFirst` es lo único que cambia entre los dos: lo que no entra en lo que falta entregar
// se le suma al primer renglón que lo admite (el aviso previo, FR-026) o queda sin asignar (el
// reparto del POS, que eso lo cobra). `perLine` trae, por línea y en el orden del pedido, cuántas
// unidades entraron en lo que faltaba (`within`).
const allocate = (summary, cartLines, menuByCode, overflowToFirst) => {
    const slots = ((summary && summary.rules) || []).map((rule) => ({
        rule,
        kind: rule.matchKind,
        values: rule.matchKind === 'products'
            ? (rule.matchValue || []).map((v) => Number(v))
            : (rule.matchValue || []).map((v) => String(v)),
        allowance: Number(rule.allowance) || 0,
        before: Number(rule.delivered) || 0,
        delivered: Number(rule.delivered) || 0,
        withinRoom: 0,
    }));
    const ofItems = slots.filter((slot) => slot.kind === 'products');
    const ofCategory = slots.filter((slot) => slot.kind === 'category');

    const outside = [];
    const outsideByCode = new Map();
    const perLine = [];

    (cartLines || []).forEach((line) => {
        const code = Number(line.code);
        let left = Number(line.quantity) || 0;
        if (!(left > 0)) return;
        const quantity = left;
        const item = lookup(menuByCode, code);

        const candidates = !item ? [] : [
            ...(item.deleted ? [] : ofItems.filter((slot) => slot.values.includes(code))),
            ...ofCategory.filter((slot) => slot.values.includes(String(item.category))),
        ];

        if (candidates.length === 0) {
            let row = outsideByCode.get(code);
            if (!row) {
                row = { code, name: item && item.name ? item.name : null, quantity: 0 };
                if (!item) row.unknown = true;
                outsideByCode.set(code, row);
                outside.push(row);
            }
            row.quantity += left;
            perLine.push({ code, quantity, within: 0 });
            return;
        }

        // Por tramos y no unidad por unidad, que da lo mismo: un renglón sigue siendo "el primero
        // al que le falta" hasta que se completa.
        candidates.forEach((slot) => {
            if (left <= 0) return;
            const room = slot.allowance - slot.delivered;
            if (room <= 0) return;
            const units = Math.min(left, room);
            slot.delivered += units;
            slot.withinRoom += units;
            left -= units;
        });
        perLine.push({ code, quantity, within: quantity - left });
        if (left > 0 && overflowToFirst) candidates[0].delivered += left;
    });

    return { slots, outside, perLine };
};

const rowsAfter = (slots) => slots.map((slot) => ({
    ...slot.rule,
    allowance: slot.allowance,
    delivered: slot.delivered,
    over: Math.max(0, slot.delivered - slot.allowance),
    added: slot.delivered - slot.before,
}));

// El aviso previo del POS: cómo quedaría el contador con el pedido del carrito.
//
//   summary     el resumen en pantalla; `rules` viene en el orden del resumen y cada renglón trae
//               lo ya entregado.
//   cartLines   [{ code, quantity }], en el orden del carrito.
//   menuByCode  código → { name, category, deleted? } (ver `menuByCodeOf`).
//
// La regla es la de FR-026, igual que en el backend: para cada unidad, entre los renglones que la
// admiten van primero los de ítems y después los de categoría, cada grupo en el orden del
// resumen; va al primero al que todavía le falte entregar y, si a ninguno le falta, al primero.
// Si ninguno la admite queda fuera de lo pactado. Un renglón de ítems admite un código sólo si el
// ítem está en el menú y no está eliminado; uno de categoría, si la categoría del ítem está en su
// lista.
//
// Devuelve:
//   after     todos los renglones como quedarían ({ …renglón, delivered, over, added }): sirve
//             tal cual para `formatCounter({ rules: after })`.
//   exceeded  los de `after` que este pedido deja pasados de lo pactado — los que recibieron
//             alguna unidad y terminan con más entregado que pactado (FR-064). Un renglón que ya
//             estaba excedido y que el pedido no toca no se vuelve a avisar.
//   outside   [{ code, name, quantity }]: lo que ningún renglón admite, por ítem (FR-065). Un
//             código que NO está en `menuByCode` va acá con `unknown: true`: de ése la pantalla
//             no sabe la categoría, así que no puede afirmar que quede fuera. El server lo busca
//             en su propia ficha, donde un ítem dado de baja sigue contando por categoría.
//   unused    [{ label, remaining }]: renglones a los que el pedido les sumaría algo dentro de lo
//             que todavía les falta, con lo que les falta ANTES de este pedido. Es el aviso de
//             "Cargar a la mesa" (FR-066): "Pizza: quedan 6 incluidas sin usar".
export const previewOrder = (summary, cartLines, menuByCode) => {
    const { slots, outside } = allocate(summary, cartLines, menuByCode, true);

    const after = rowsAfter(slots);
    const exceeded = after.filter((row) => row.added > 0 && row.delivered > row.allowance);
    const unused = slots
        .filter((slot) => slot.withinRoom > 0)
        .map((slot) => ({ label: slot.rule.label, remaining: slot.allowance - slot.before }));

    return { after, exceeded, outside, unused };
};

// El reparto del POS en una mesa de un evento con inclusiones: del pedido, qué sale sin cargo y
// qué va a la cuenta de la mesa. Es el mismo reparto de `previewOrder`, con una diferencia: lo
// que no entra en lo que a un renglón le falta entregar NO se le suma al renglón — es un extra,
// igual que lo que ningún renglón admite (o un código que no está en el menú a mano). El server
// aplica la misma regla y rechaza el pedido incluido que no entre entero (7609).
//
// Devuelve:
//   byCode    código → { included, extra }: cuántas unidades de ese ítem van por la entrada y
//             cuántas se cobran. La suma es la cantidad pedida.
//   after     el contador como quedaría sólo con lo incluido (misma forma que en `previewOrder`).
//   included  total de unidades incluidas;  extra  total de unidades que se cobran.
export const splitOrder = (summary, lines, menuByCode) => {
    // Por código ascendente, como el server reparte el pedido incluido antes de medirlo: con
    // renglones que comparten ítems el orden cambia qué entra, y repartir en el orden del carrito
    // armaba un pedido "incluido" que el server rechazaba (7609) una y otra vez.
    const ordered = [...(lines || [])].sort((a, b) => Number(a.code) - Number(b.code));
    const { slots, perLine } = allocate(summary, ordered, menuByCode, false);
    const byCode = {};
    let included = 0;
    let extra = 0;
    perLine.forEach((line) => {
        if (!byCode[line.code]) byCode[line.code] = { included: 0, extra: 0 };
        byCode[line.code].included += line.within;
        byCode[line.code].extra += line.quantity - line.within;
        included += line.within;
        extra += line.quantity - line.within;
    });
    return { byCode, after: rowsAfter(slots), included, extra };
};

// ¿Al evento le queda algo incluido por entregar? De esto sale el Cobro por defecto del POS en
// una mesa del evento: "Incluido" mientras a algún renglón le falte, "Cargar a la mesa" después.
export const hasRoomLeft = (summary) => ((summary && summary.rules) || [])
    .some((rule) => (Number(rule.delivered) || 0) < (Number(rule.allowance) || 0));

// ¿El evento pactó cantidades? Es lo que mira el salón para ofrecer "Sentar a todos".
export const hasInclusions = (summary) => !!summary && Array.isArray(summary.rules) && summary.rules.length > 0;

// ¿Dos renglones pueden admitir un mismo ítem? Dos de categoría, si comparten una categoría; dos
// de ítems, si comparten un código; uno de cada clase, si algún código del de ítems es de una
// categoría del otro. Un código que no está en el menú a mano cuenta como que sí: no se sabe.
const rulesOverlap = (a, b, menuByCode) => {
    const valuesOf = (rule) => (rule.matchValue || []).map((v) => (rule.matchKind === 'products' ? Number(v) : String(v)));
    if (a.matchKind === b.matchKind) {
        const mine = valuesOf(a);
        return valuesOf(b).some((v) => mine.includes(v));
    }
    const ofItems = a.matchKind === 'products' ? a : b;
    const categories = valuesOf(a.matchKind === 'products' ? b : a);
    return valuesOf(ofItems).some((code) => {
        const item = lookup(menuByCode, code);
        return !item || categories.includes(String(item.category));
    });
};

// El aviso previo de "Pasar a incluido" (FR-098): cómo quedaría el contador si una comanda que YA
// está cargada a la mesa pasara a contar como incluida. Es `previewOrder` más las dos cosas que
// la pantalla no puede saber de una comanda vieja, y que el server sí:
//
//   · Qué es un ítem que ya no está en el menú a mano (se dio de baja después de cargarlo, o se
//     creó después de pedir el menú). El server lo lee de su ficha y, dado de baja, lo sigue
//     contando en un renglón por categoría. Esos van a `unverified` y NO a `outside`.
//   · En qué lugar del orden de alta entra. El server reparte TODAS las líneas del evento por
//     número de venta, y una comanda vieja entra antes que las incluidas después que ella; el
//     POS no tiene este problema porque su pedido es siempre el último. Sumarla al final da el
//     mismo resultado sólo si ningún renglón que la comanda toca comparte ítems con otro.
//
// Con cualquiera de las dos, `uncertain` es true y `exceeded` viene vacío: no se nombra un
// renglón ni un número que el server puede dejar distinto. Sin ninguna, `exceeded` y `outside`
// son exactamente los del server.
//
// Devuelve { exceeded, outside, unverified, overlapping, uncertain }; `overlapping` son las
// etiquetas de los renglones que comparten ítems con el que la comanda toca.
export const previewCorrection = (summary, orderLines, menuByCode) => {
    const preview = previewOrder(summary, orderLines, menuByCode);
    const unverified = preview.outside.filter((row) => row.unknown);
    const outside = preview.outside.filter((row) => !row.unknown);

    const overlapping = [];
    preview.after.forEach((row, index) => {
        if (!(row.added > 0)) return;
        preview.after.forEach((other, otherIndex) => {
            if (otherIndex === index || !rulesOverlap(row, other, menuByCode)) return;
            [row.label, other.label].forEach((label) => { if (!overlapping.includes(label)) overlapping.push(label); });
        });
    });

    const uncertain = unverified.length > 0 || overlapping.length > 0;
    return { exceeded: uncertain ? [] : preview.exceeded, outside, unverified, overlapping, uncertain };
};
